import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { validateTokenSize } from "../../utils/storageUtils";

interface User {
  id: string;
  login: string;
  role: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem("token"),
  refreshToken: localStorage.getItem("refreshToken"),
  isAuthenticated: !!localStorage.getItem("token"),
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        refreshToken: string;
      }>
    ) => {
      const { token, refreshToken, user } = action.payload;

      // Критическая проверка роли - только MERCHANT может входить в merchant panel
      if (user.role !== "MERCHANT") {
        state.error = `Доступ запрещен. Эта панель предназначена только для мерчантов. Ваша роль: ${user.role}`;
        state.isAuthenticated = false;
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        return;
      }

      if (!validateTokenSize(token)) {
        state.error = "Token слишком большой";
        return;
      }

      if (!validateTokenSize(refreshToken)) {
        state.error = "Refresh token слишком большой";
        return;
      }

      state.isLoading = false;
      state.user = user;
      state.token = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      state.error = null;

      localStorage.setItem("token", token);
      localStorage.setItem("refreshToken", refreshToken);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;

      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;

      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
    },
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    updateTokens: (
      state,
      action: PayloadAction<{ token: string; refreshToken: string }>
    ) => {
      const { token, refreshToken } = action.payload;

      if (!validateTokenSize(token) || !validateTokenSize(refreshToken)) {
        state.error = "Токены слишком большие";
        state.isAuthenticated = false;
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        return;
      }

      state.token = token;
      state.refreshToken = refreshToken;

      localStorage.setItem("token", token);
      localStorage.setItem("refreshToken", refreshToken);
    },
    initializeAuth: (state) => {
      const token = localStorage.getItem("token");
      const refreshToken = localStorage.getItem("refreshToken");

      if (token && refreshToken) {
        state.token = token;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        state.isLoading = false;
      } else {
        state.isAuthenticated = false;
        state.isLoading = false;
      }
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  setUser,
  updateTokens,
  initializeAuth,
} = authSlice.actions;

export default authSlice.reducer;
